import type { FeatureToggle } from 'clients/UserClient/types';

import { FeatureToggleType } from '@/features/feature-toggle';
import { useQuery } from '@tanstack/react-query';
import { userClient } from 'clients/UserClient/UserClient';
import { useCallback } from 'react';
import { QueryKey } from 'types/query';
import { useCurrentUserQuery } from './useCurrentUserQuery';

export { FeatureToggleType };

const forceEnableFeatureToggles = [
  FeatureToggleType.ALL_UNLOCK_AI_MESSAGE,
  FeatureToggleType.WHATSAPP_UNLOCK_AI_MESSAGE,
  FeatureToggleType.LINKEDIN_IN_MAIL_UNLOCK_AI_MESSAGE,
  FeatureToggleType.INDEED_UNLOCK_AI_MESSAGE,
];

const shouldAllowFeatureToggle = (featureToggleName: string) => {
  const isAiMessagingForcedOff = process.env.REACT_APP_AI_MESSAGING_FORCE_OFF === '1';

  switch (featureToggleName) {
    case FeatureToggleType.ALL_UNLOCK_AI_MESSAGE:
    case FeatureToggleType.WHATSAPP_UNLOCK_AI_MESSAGE:
    case FeatureToggleType.LINKEDIN_IN_MAIL_UNLOCK_AI_MESSAGE:
    case FeatureToggleType.INDEED_UNLOCK_AI_MESSAGE:
      return !isAiMessagingForcedOff;
    default:
      return true;
  }
};

export type UseFeatureTogglesHook = () => {
  getFeatureToogleValue: (
    feature: string | ((ft_type: typeof FeatureToggleType) => FeatureToggleType),
  ) => string | undefined;
  isFeatureToggleEnabled: (feature: string | ((ft_type: typeof FeatureToggleType) => FeatureToggleType)) => boolean;
  isFeatureToggleEnabledByPrefix: (feature: string) => boolean;
  isLoading: boolean;
  dataUpdatedAt: number;
};

export const useFeatureToggles: UseFeatureTogglesHook = () => {
  const { data: currentUser } = useCurrentUserQuery();

  const {
    data: featureToggles,
    isFetching: featureTogglesIsLoading,
    dataUpdatedAt,
  } = useQuery(
    [QueryKey.useFeatureTogglesQuery],
    async () => {
      const { data: featureToggles } = await userClient.getFeatureToggles();

      return featureToggles;
    },
    { enabled: Boolean(currentUser), initialData: [], initialDataUpdatedAt: 0 },
  );

  const getFeatureToogleValue = useCallback<ReturnType<UseFeatureTogglesHook>['getFeatureToogleValue']>(
    (feature) => {
      const name = typeof feature === 'string' ? feature : feature(FeatureToggleType);

      if (!shouldAllowFeatureToggle(name)) return undefined;

      const featureToggle = featureToggles.find((ft) => ft.name === name);
      const isForced = forceEnableFeatureToggles.some((f) => f === name);

      return isForced || featureToggle?.enabled ? featureToggle?.value : undefined;
    },
    [featureToggles, currentUser?.tenant?.id],
  );

  const isFeatureToggleEnabled = useCallback<ReturnType<UseFeatureTogglesHook>['isFeatureToggleEnabled']>(
    (feature) => {
      const name = typeof feature === 'string' ? feature : feature(FeatureToggleType);

      if (!shouldAllowFeatureToggle(name)) return false;

      const isForced = forceEnableFeatureToggles.some((f) => f === name);
      const isEnabledByUser = featureToggles.some((ft) => ft.name === name && ft.enabled);
      const isFeatureToggleEnabled = isForced || isEnabledByUser;

      if (name === 'search_templates') {
        return __legacy_validateSearchTemplatesToggle(featureToggles);
      }

      return isFeatureToggleEnabled;
    },
    [featureToggles, currentUser?.tenant?.id],
  );

  const isFeatureToggleEnabledByPrefix = useCallback<
    ReturnType<UseFeatureTogglesHook>['isFeatureToggleEnabledByPrefix']
  >(
    (feature) =>
      featureToggles.some((featureToggle) => {
        if (!shouldAllowFeatureToggle(featureToggle.name)) return false;

        const matchesPrefix = featureToggle.name.startsWith(feature);
        const isForced = forceEnableFeatureToggles.some((f) => f === featureToggle.name);

        return (matchesPrefix && featureToggle.enabled) || (isForced && matchesPrefix);
      }),
    [featureToggles, currentUser?.tenant?.id],
  );

  return {
    isFeatureToggleEnabled,
    isFeatureToggleEnabledByPrefix,
    getFeatureToogleValue,
    isLoading: featureTogglesIsLoading || !currentUser,
    dataUpdatedAt,
  };
};

export default useFeatureToggles;

//#region Legacy feature toggles
// Needs to be studied if they can be removed. Theorically, they're useless because the feature(s) is/are already released

const __legacy_validateSearchTemplatesToggle = (featureToggles: FeatureToggle[]) => {
  const searchTemplateFeatureToggleSet = featureToggles.find((feature) => feature.name === 'search_templates');
  const isFeatureToggleEnabled = Boolean(searchTemplateFeatureToggleSet?.enabled);
  const searchTemplatesReleaseToggleEnabled = process.env.REACT_APP_SEARCH_TEMPLATES_RELEASE_TOGGLE === 'on';

  console.debug('[useFeatureToggles] release toggle "search_templates": ', {
    isFeatureToggleEnabled,
    isReleaseToggleEnabled: searchTemplatesReleaseToggleEnabled,
  });

  return (
    searchTemplateFeatureToggleSet?.enabled ||
    (searchTemplateFeatureToggleSet?.enabled !== false && searchTemplatesReleaseToggleEnabled)
  );
};
